import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Button,
  Box,
  Typography
} from '@mui/material';
import { getValidSemesters, getOrdinal } from '../utils/dateUtils.js';

/**
 * AddCourseDialog component for adding custom courses
 * @param {boolean} open - Whether the dialog is open
 * @param {Function} onClose - Function to handle dialog close
 * @param {Function} onAddCourse - Function to handle adding a course
 * @param {Object} prefilledData - Prefilled data for year, semester, period
 */
const AddCourseDialog = ({ open, onClose, onAddCourse, prefilledData = {} }) => {
  const [formData, setFormData] = useState({
    course_name: '',
    course_code: '',
    credits: '',
    year: prefilledData.year?.toString() || '',
    semester: prefilledData.semester?.toString() || '',
    periods: prefilledData.period ? [prefilledData.period] : []
  });

  // Reset form when dialog opens/closes or prefilledData changes
  React.useEffect(() => {
    if (open) {
      setFormData({
        course_name: '',
        course_code: '',
        credits: '',
        year: prefilledData.year?.toString() || '',
        semester: prefilledData.semester?.toString() || '',
        periods: prefilledData.period ? [prefilledData.period] : []
      });
    }
  }, [open, prefilledData]);

  const handleClose = () => {
    setFormData({
      course_name: '',
      course_code: '',
      credits: '',
      year: '',
      semester: '',
      periods: []
    });
    onClose();
  };

  const handleFormChange = (field, value) => {
    const newFormData = {
      ...formData,
      [field]: value
    };
    
    // If year changes, reset semester to first valid option
    if (field === 'year') {
      const validSemesters = getValidSemesters(value);
      newFormData.semester = validSemesters.length > 0 ? validSemesters[0].toString() : '';
    }
    
    setFormData(newFormData);
  };

  const handlePeriodToggle = (period, checked) => {
    setFormData(prev => ({
      ...prev,
      periods: checked 
        ? [...prev.periods, period].sort((a, b) => a - b)
        : prev.periods.filter(p => p !== period)
    }));
  };

  const handleAddCourse = () => {
    // Validate required fields
    if (!formData.course_name || !formData.course_code || !formData.credits || 
        !formData.year || !formData.semester || formData.periods.length === 0) {
      alert('Please fill in all required fields');
      return;
    }

    // Validate year/semester combination
    const validSemesters = getValidSemesters(formData.year);
    if (!validSemesters.includes(parseInt(formData.semester))) {
      alert(`Invalid semester for year ${formData.year}. Valid semesters: ${validSemesters.join(', ')}`);
      return;
    }

    // Create new course object
    const newCourse = {
      course_name: formData.course_name,
      course_code: formData.course_code,     
      credits: parseFloat(formData.credits),
      instances: [{
        year: parseInt(formData.year),
        semester: parseInt(formData.semester),
        periods: formData.periods,
        ...(formData.periods.length > 1 && {
          period_credits: formData.periods.map(() => parseFloat(formData.credits) / formData.periods.length)
        })
      }],
      notes: "MANUALLY ADDED.",
      isCustomCourse: true
    };

    onAddCourse(newCourse, formData);
    handleClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Add Other Course</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <TextField
            label="Course Name"
            value={formData.course_name}
            onChange={(e) => handleFormChange('course_name', e.target.value)}
            required
            fullWidth
          />
          
          <TextField
            label="Course Code"
            value={formData.course_code}
            onChange={(e) => handleFormChange('course_code', e.target.value)}
            required
            fullWidth
          />
          
          <TextField
            label="Total Credits"
            type="number"
            value={formData.credits}
            onChange={(e) => handleFormChange('credits', e.target.value)}
            required
            fullWidth
            inputProps={{ min: 0.5, step: 0.5 }}
          />
          
          <Box display="flex" gap={2}>
            <FormControl fullWidth required>
              <InputLabel>Year</InputLabel>
              <Select
                value={formData.year}
                onChange={(e) => handleFormChange('year', e.target.value)}
                label="Year"
              >
                <MenuItem value="2025">2025</MenuItem>
                <MenuItem value="2026">2026</MenuItem>
                <MenuItem value="2027">2027</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl fullWidth required>
              <InputLabel>Semester</InputLabel>
              <Select
                value={formData.semester}
                onChange={(e) => handleFormChange('semester', e.target.value)}
                label="Semester"
              >
                {getValidSemesters(formData.year).map(semester => (
                  <MenuItem key={semester} value={semester.toString()}>
                    {semester}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          
          <Typography variant="caption" color="textSecondary" sx={{ mt: -1 }}>
            Available semesters: 2025 (S1), 2026 (S2, S3), 2027 (S4)
          </Typography>
          
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Periods (select one or more):
            </Typography>
            <Box display="flex" gap={1}>
              {[1, 2].map(period => (
                <FormControlLabel
                  key={period}
                  control={
                    <Checkbox
                      checked={formData.periods.includes(period)}
                      onChange={(e) => handlePeriodToggle(period, e.target.checked)}
                    />
                  }
                  label={`${getOrdinal(period)} Period`}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleAddCourse} variant="contained">
          Add Course
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCourseDialog; 